import { useShiftEditor } from '../../useShiftEditor'
import { useFormContext } from 'react-hook-form'
import { PaymentForm, PaymentTypeEnum } from '../../schemas'
import { Box, Text, Message, Flex } from 'ui'
import { CircleDollarSign } from 'lucide-react'
import { useTheme } from 'styled-components'
import { EmployerGuidance } from 'typings/common_defs'
import { formatCurrency } from 'lib/string'

interface PayGuidanceProps {
  employerGuidance?: EmployerGuidance
  isLoading: boolean
  shiftDurationHours: number
}

type RemainingBonusInput = {
  minimumTotalCompensation: number
  payType: PaymentTypeEnum
  // Form values are only coerced to numbers on submit, so live values may be strings
  pay?: number | string
  bonus?: number | string
  customBonus?: number | string
  shiftDurationHours: number
}

function getRemainingBonus({
  minimumTotalCompensation,
  payType,
  pay,
  bonus,
  customBonus,
  shiftDurationHours,
}: RemainingBonusInput) {
  const basePay = Number(pay) || 0
  const totalBonuses = (Number(bonus) || 0) + (Number(customBonus) || 0)
  const baseCompensation =
    payType === PaymentTypeEnum.Hourly ? basePay * shiftDurationHours : basePay
  const totalCompensation = baseCompensation + totalBonuses
  return Math.max(minimumTotalCompensation - totalCompensation, 0)
}

export function PayGuidance({
  employerGuidance,
  isLoading,
  shiftDurationHours,
}: PayGuidanceProps) {
  const { shiftData } = useShiftEditor()
  const { colors } = useTheme()
  const { watch } = useFormContext<PaymentForm>()
  const payType = watch('paymentType')
  const pay = watch('pay')
  const bonus = watch('bonus')
  const customBonus = watch('customBonus')
  const numShiftLeads = shiftData.staff?.numShiftLeads

  if (isLoading) {
    return (
      <Message variant={'fixed'} hideIcon={true}>
        <Flex>
          <Box flexShrink={0} pr={2}>
            <CircleDollarSign size={24} color={colors.primary} />
          </Box>
          <Box>
            <Text fontWeight={2}>Calculating pay guidance...</Text>
          </Box>
        </Flex>
      </Message>
    )
  }

  if (
    !employerGuidance ||
    !employerGuidance.minimumPayRate ||
    !employerGuidance.minimumPayLumpSum
  ) {
    return null
  }

  const minimumTotalCompensation =
    payType === PaymentTypeEnum.Hourly
      ? employerGuidance.minimumPayRate * shiftDurationHours
      : employerGuidance.minimumPayLumpSum
  const remainingBonus = getRemainingBonus({
    minimumTotalCompensation,
    payType,
    pay,
    bonus,
    customBonus,
    shiftDurationHours,
  })

  const shouldShowBonus = remainingBonus > 0 && !employerGuidance.hideBonus

  return (
    <Message variant={'fixed'} hideIcon={true}>
      <Flex>
        <Box flexShrink={0} pr={2}>
          <CircleDollarSign size={24} color={colors.primary} />
        </Box>
        <Box>
          <Text fontWeight={2}>Recommended Pay</Text>
          <Box mt={1}>
            {`For driving jobs in California, regulations require that the total compensation for this shift must be at least ${formatCurrency(
              minimumTotalCompensation
            )}.`}
            {shouldShowBonus
              ? ` You may need to offer a bonus of ${formatCurrency(
                  remainingBonus
                )} to ensure the total compensation meets the minimum requirement.`
              : ''}
            {numShiftLeads
              ? ' And we recommend paying shift leads 20% higher.'
              : null}
          </Box>
        </Box>
      </Flex>
    </Message>
  )
}
